import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// URL pour récupérer les données depuis Preview (doit être configuré via env)
const PREVIEW_BACKEND = process.env.REACT_APP_PREVIEW_BACKEND_URL;

const SyncAdmin = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  // Fetch current database status
  const fetchCurrentStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sync-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch status');
      
      const data = await response.json();
      setCurrentStatus(data);
    } catch (err) {
      console.error('Error fetching status:', err);
      setError('Impossible de récupérer le statut actuel');
    }
  };

  // Télécharger les données depuis Preview
  const downloadPreviewData = async () => {
    try {
      // On peut essayer de récupérer les données via une route d'export sur Preview
      // Pour l'instant, on va utiliser le fichier statique
      const response = await fetch('/preview_data.json');
      if (!response.ok) throw new Error('Fichier preview_data.json non trouvé');
      const data = await response.json();
      setPreviewData(data);
      return data;
    } catch (err) {
      console.error('Erreur téléchargement preview:', err);
      throw new Error('Impossible de télécharger les données du preview');
    }
  };

  useEffect(() => {
    if (token) {
      fetchCurrentStatus();
    }
  }, [token]);

  const handleImportData = async () => {
    // Action directe sans confirmation

    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      // Télécharger les données du preview si pas encore fait
      let dataToImport = previewData;
      if (!dataToImport) {
        console.log('Téléchargement des données du preview...');
        dataToImport = await downloadPreviewData();
      }

      console.log('Envoi des données au backend...');
      const response = await fetch(`${BACKEND_URL}/api/admin/import-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ data: dataToImport })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erreur serveur:', errorText);
        throw new Error(errorText || 'Import failed');
      }

      const result = await response.json();
      setStatus(result);
      
      // Refresh current status
      await fetchCurrentStatus();
      
      toast.success("Synchronisation réussie! Les données ont été importées avec succès.");
    } catch (err) {
      console.error('Import error:', err);
      setError(err.message);
      toast.error(`Erreur lors de l'import: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/export-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const data = await response.json();
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Export réussi! Le fichier a été téléchargé.");
    } catch (err) {
      console.error('Export error:', err);
      setError(err.message);
      toast.error(`Erreur lors de l'export: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">
        🔄 Synchronisation Preview → Production
      </h1>

      {/* Current Status */}
      {currentStatus && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            📊 État Actuel de la Base de Données
          </h2>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">
              Base de données: <span className="font-mono font-semibold">{currentStatus.database}</span>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {Object.entries(currentStatus.collections).map(([collection, count]) => (
                <div key={collection} className="bg-white rounded p-3 border border-gray-200">
                  <div className="text-xs text-gray-500 uppercase">{collection}</div>
                  <div className="text-2xl font-bold text-gray-900">{count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Preview Data Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-blue-900 mb-4">
          📦 Données du Preview Prêtes à Importer
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded p-3 border border-blue-200">
            <div className="text-xs text-blue-600 uppercase">Missions</div>
            <div className="text-2xl font-bold text-blue-900">147</div>
          </div>
          <div className="bg-white rounded p-3 border border-blue-200">
            <div className="text-xs text-blue-600 uppercase">Clients</div>
            <div className="text-2xl font-bold text-blue-900">43</div>
          </div>
          <div className="bg-white rounded p-3 border border-blue-200">
            <div className="text-xs text-blue-600 uppercase">Logements</div>
            <div className="text-2xl font-bold text-blue-900">89</div>
          </div>
          <div className="bg-white rounded p-3 border border-blue-200">
            <div className="text-xs text-blue-600 uppercase">Employés</div>
            <div className="text-2xl font-bold text-blue-900">16</div>
          </div>
        </div>
        <p className="text-sm text-blue-700 mt-4">
          Ces données seront importées depuis l'environnement preview.
        </p>
      </div>

      {/* Actions */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">
          ⚡ Actions de Synchronisation
        </h2>
        
        <div className="space-y-4">
          {/* Import Button */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">
              📥 Importer les Données du Preview
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Remplace toutes les données actuelles par les 147 missions, 43 clients, 89 logements, etc. du preview.
            </p>
            <button
              onClick={handleImportData}
              disabled={loading}
              className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {loading ? '⏳ Import en cours...' : '📥 Importer les Données du Preview'}
            </button>
          </div>

          {/* Export Button */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">
              📤 Exporter les Données Actuelles
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Crée une sauvegarde des données actuelles en JSON.
            </p>
            <button
              onClick={handleExportData}
              disabled={loading}
              className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? '⏳ Export en cours...' : '📤 Exporter (Sauvegarde)'}
            </button>
          </div>

          {/* Refresh Status Button */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">
              🔄 Actualiser le Statut
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Recharge les informations de la base de données actuelle.
            </p>
            <button
              onClick={fetchCurrentStatus}
              disabled={loading}
              className={`px-6 py-3 rounded-lg font-semibold text-white transition-colors ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
            >
              🔄 Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Status Display */}
      {status && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-green-900 mb-4">
            ✅ Import Réussi!
          </h2>
          <p className="text-green-800 mb-4">{status.message}</p>
          <div className="bg-white rounded p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Collections Importées:</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(status.imported_collections).map(([collection, count]) => (
                <div key={collection} className="text-sm">
                  <span className="font-semibold">{collection}:</span> {count}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-red-900 mb-2">
            ❌ Erreur
          </h2>
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-yellow-900 mb-4">
          ⚠️ Important
        </h2>
        <ul className="list-disc list-inside space-y-2 text-yellow-800 text-sm">
          <li>Cette opération remplace TOUTES les données actuelles</li>
          <li>Créez une sauvegarde avant d'importer (bouton "Exporter")</li>
          <li>L'opération est irréversible sans sauvegarde</li>
          <li>Tous les utilisateurs doivent être déconnectés pendant l'import</li>
          <li>L'import prend environ 30 secondes</li>
        </ul>
      </div>
    </div>
  );
};

export default SyncAdmin;
